import { useEffect } from 'react';
import i18n from '../i18n';

export type LangParam = 'zh' | 'en' | 'es';

export function normalizeLangParam(raw: string | null | undefined): LangParam | undefined {
    if (!raw) return undefined;
    const v = raw.trim().toLowerCase();
    if (v === 'zh' || v === 'en' || v === 'es') return v;
    return undefined;
}

export function langParamToI18nLng(lang: LangParam): 'zh-CN' | 'en-US' | 'es-ES' {
    switch (lang) {
        case 'zh':
            return 'zh-CN';
        case 'es':
            return 'es-ES';
        case 'en':
        default:
            return 'en-US';
    }
}

export function i18nLngToLangParam(lng: string | null | undefined): LangParam {
    const v = (lng || 'en').toLowerCase();
    if (v.includes('zh')) return 'zh';
    if (v.includes('es')) return 'es';
    return 'en';
}

/**
 * Native 子页面启动时校准语言。
 *
 * 背景：i18next 的 LanguageDetector 在 init 阶段会按 localStorage -> navigator 检测语言。
 * 若 native 注入的 lang 与 localStorage 不一致，且我们在 init 之后才 changeLanguage，
 * 会出现首屏偶发语言不一致的问题。
 *
 * 该 hook 仅在 native webview 打开的 /native 页面中调用：
 * - 从 URL ?lang= 或 window.__nativeParams__.lang 解析目标语言（仅支持 zh/en/es）
 * - 若与当前 i18n.language 不一致，则写入 localStorage(i18nextLng) 并 reload
 */
export function useNativeLangSync(): void {
    useEffect(() => {
        try {
            const sp = new URLSearchParams(window.location.search);
            const urlLang = normalizeLangParam(sp.get('lang'));
            const nativeParams = (window as any).__nativeParams__ as { lang?: unknown } | undefined;
            const nativeLang = normalizeLangParam(
                typeof nativeParams?.lang === 'string' ? nativeParams.lang : undefined
            );

            const resolved = urlLang ?? nativeLang;
            if (!resolved) {
                return;
            }

            const target = langParamToI18nLng(resolved);
            if (i18n.language === target) {
                return;
            }

            localStorage.setItem('i18nextLng', target);
            window.location.reload();
        } catch {
            // ignore
        }
    }, []);
}

